/* ==========================================================================
   MOBILE DOCK — behaviour
   ==========================================================================
   Integrates with this theme's REAL components instead of re-implementing
   or simulating them:

     - Cart drawer   : calls .openMenuDrawer() directly on the existing
                       <mini-cart> or <cart-drawer> custom element.
     - Menu drawer   : calls .openMenuDrawer() directly on the existing
                       <header-drawer> custom element.
     - Language /
       Currency      : opens that same menu drawer, then focuses the
                       theme's existing .menu-drawer__localization form —
                       no localization logic is duplicated.
     - Cart badge    : never polls /cart.js. Listens for the events this
                       theme already dispatches on add-to-cart
                       ('ajaxProduct:added', 'cart:added') and updates
                       from cart.item_count rendered at page load
                       otherwise.

   NOTE ON SEARCH: this theme's predictive-search.js / header markup
   were not part of the files reviewed while building this component, so
   there's no confirmed custom element to call directly for it (unlike
   cart/menu, which are proven above). SEARCH_TRIGGER_SELECTORS below is
   a best-effort list of common selectors for an existing header search
   toggle — if it doesn't match your header, the dock falls back to
   navigating to the search page, and you can add your real selector to
   the top of this list.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__helmaMobileDockInitialized) return;
  window.__helmaMobileDockInitialized = true;

  var CONFIG = {
    searchTriggerSelectors: [
      '[data-search-drawer-toggle]',
      'summary[aria-controls*="search" i]',
      '.header__icon--search',
      'button[aria-label*="Search" i]'
    ],
    animationDurationFallback: 260
  };

  function findFirst(selectors) {
    for (var i = 0; i < selectors.length; i++) {
      try {
        var el = document.querySelector(selectors[i]);
        if (el) return el;
      } catch (e) { /* invalid selector in this DOM, skip */ }
    }
    return null;
  }

  function openDrawerElement(el) {
    if (!el) return false;

    var details = el.querySelector('details');
    if (details && details.hasAttribute('open')) return true;

    var summary = details && details.querySelector('summary');
    if (summary) {
      window.setTimeout(function () {
        if (!details.hasAttribute('open')) summary.click();
      });
      return true;
    }

    if (typeof el.openMenuDrawer === 'function') {
      el.openMenuDrawer();
      return true;
    }
    return false;
  }

  function openCartDrawer() {
    var el = document.querySelector('cart-drawer');
    return openDrawerElement(el);
  }

  function openMenuDrawerAndFocusLocalization(type) {
    var el = document.querySelector('header-drawer');
    var opened = openDrawerElement(el);
    if (!opened) return false;

    window.setTimeout(function () {
      var localizationBlock = document.querySelector('.menu-drawer__localization');
      if (!localizationBlock) return;
      localizationBlock.scrollIntoView({ block: 'center', behavior: 'smooth' });

      var forms = localizationBlock.querySelectorAll('.disclosure');
      var target = forms[0];
      if (type === 'currency' && forms.length > 1) target = forms[1];

      var opener = target && target.querySelector('summary, button');
      if (opener) opener.focus({ preventScroll: true });
    }, 350);

    return true;
  }

  function runCustomJs(itemEl) {
    var script = itemEl.querySelector('script[data-mobile-dock-js]');
    if (!script) return;
    try {
      // eslint-disable-next-line no-new-func
      var fn = new Function(script.textContent);
      fn.call(itemEl);
    } catch (err) {
      console.error('[mobile-dock] custom JS action failed:', err);
    }
  }

  /* ---------------------------------------------------------------------
     Cart badge — SSR value kept live via real theme events
     --------------------------------------------------------------------- */
  function setCartBadgeCount(count) {
    document.querySelectorAll('[data-mobile-dock-badge="cart_count"]').forEach(function (badge) {
      badge.dataset.dockUpdatedThisTick = 'true';
      window.requestAnimationFrame(function () {
        delete badge.dataset.dockUpdatedThisTick;
      });

      var valueEl = badge.querySelector('[data-mobile-dock-badge-value]');
      if (valueEl) valueEl.textContent = count;
      if (count > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
  }

  function extractCountFromCartPayload(payload) {
    if (!payload) return null;
    if (typeof payload.item_count === 'number') return payload.item_count;
    if (payload.cart && typeof payload.cart.item_count === 'number') return payload.cart.item_count;
    return null;
  }

  document.addEventListener('ajaxProduct:added', function (event) {
    var count = extractCountFromCartPayload(event.detail && event.detail.product);
    if (count !== null) setCartBadgeCount(count);
  });

  // Fired by theme.liquid on every successful add — no payload, so just
  // nudge the badge up by one as an optimistic update if we can't read a
  // real count from it.
  document.addEventListener('cart:added', function () {
    document.querySelectorAll('[data-mobile-dock-badge="cart_count"]').forEach(function (badge) {
      var valueEl = badge.querySelector('[data-mobile-dock-badge-value]');
      if (!valueEl) return;
      var current = parseInt(valueEl.textContent, 10) || 0;
      // Only optimistically bump if nothing more authoritative already ran
      // in this tick via ajaxProduct:added.
      if (!badge.dataset.dockUpdatedThisTick) {
        valueEl.textContent = current + 1;
        badge.removeAttribute('hidden');
      }
    });
  });

  /* ---------------------------------------------------------------------
     Wishlist count — no native wishlist in this theme. Any wishlist app
     can keep the badge live by dispatching:
       document.dispatchEvent(new CustomEvent('mobile-dock:wishlist-count', { detail: { count: N } }))
     --------------------------------------------------------------------- */
  document.addEventListener('mobile-dock:wishlist-count', function (event) {
    var count = event.detail && event.detail.count;
    if (typeof count !== 'number') return;
    document.querySelectorAll('[data-mobile-dock-badge="wishlist_count"]').forEach(function (badge) {
      var valueEl = badge.querySelector('[data-mobile-dock-badge-value]');
      if (valueEl) valueEl.textContent = count;
      if (count > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
    });
  });

  /* ---------------------------------------------------------------------
     Tap animation + ripple
     --------------------------------------------------------------------- */
  function playAnimation(itemEl, animName, clickEvent) {
    if (!animName || animName === 'none') return;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    if (animName === 'ripple') {
      var rect = itemEl.getBoundingClientRect();
      var ripple = document.createElement('span');
      ripple.className = 'dock-ripple';
      var size = Math.max(rect.width, rect.height);
      ripple.style.width = size + 'px';
      ripple.style.height = size + 'px';
      var x = (clickEvent ? clickEvent.clientX - rect.left : rect.width / 2) - size / 2;
      var y = (clickEvent ? clickEvent.clientY - rect.top : rect.height / 2) - size / 2;
      ripple.style.left = x + 'px';
      ripple.style.top = y + 'px';
      itemEl.appendChild(ripple);
      itemEl.classList.add('is-animating-ripple');
      window.setTimeout(function () {
        ripple.remove();
        itemEl.classList.remove('is-animating-ripple');
      }, 520);
      return;
    }

    var cls = 'is-animating-' + animName;
    itemEl.classList.remove(cls);
    // force reflow so the animation restarts on rapid repeat taps
    void itemEl.offsetWidth;
    itemEl.classList.add(cls);
    window.setTimeout(function () {
      itemEl.classList.remove(cls);
    }, CONFIG.animationDurationFallback + 300);
  }

  /* ---------------------------------------------------------------------
     Click delegation for every dock item (items + center button)
     --------------------------------------------------------------------- */
  function handleDockClick(event) {
    var itemEl = event.target.closest('[data-mobile-dock-item]');
    if (!itemEl) return;

    playAnimation(itemEl, itemEl.dataset.anim, event);

    var action = itemEl.dataset.dockAction;
    if (!action) return; // plain link — let the browser navigate normally

    switch (action) {
      case 'cart-drawer':
        if (openCartDrawer()) event.preventDefault();
        break;

      case 'menu-drawer':
        event.preventDefault();
        openDrawerElement(document.querySelector('header-drawer'));
        break;

      case 'localization-language':
        event.preventDefault();
        openMenuDrawerAndFocusLocalization('language');
        break;

      case 'localization-currency':
        event.preventDefault();
        openMenuDrawerAndFocusLocalization('currency');
        break;

      case 'search': {
        var trigger = findFirst(CONFIG.searchTriggerSelectors);
        if (trigger) {
          event.preventDefault();
          trigger.click();
        }
        // else: no match found, let the link fall through to routes.search_url
        break;
      }

      case 'wishlist':
        document.dispatchEvent(new CustomEvent('mobile-dock:wishlist-click', {
          detail: { href: itemEl.getAttribute('href') }
        }));
        if (itemEl.getAttribute('href') === '#') event.preventDefault();
        break;

      case 'custom-js':
        event.preventDefault();
        runCustomJs(itemEl);
        break;

      default:
        break;
    }
  }

  /* ---------------------------------------------------------------------
     mobile-dock custom element — scroll behaviour
     --------------------------------------------------------------------- */
  class MobileDock extends HTMLElement {
    constructor() {
      super();
      this.lastScrollY = window.scrollY;
      this.idleTimer = null;
      this.onScroll = this.onScroll.bind(this);
      this.onUserActivity = this.onUserActivity.bind(this);
      this.onDockClick = handleDockClick.bind(this);
    }

    connectedCallback() {
      this.behavior = this.dataset.scrollBehavior || 'always';
      this.autoHideDelay = (parseInt(this.dataset.autoHideDelay, 10) || 3) * 1000;
      this.addEventListener('click', this.onDockClick);

      if (this.behavior === 'hide_on_scroll') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      } else if (this.behavior === 'auto_hide') {
        this.resetIdleTimer();
        ['scroll', 'touchstart', 'click'].forEach((evt) => {
          window.addEventListener(evt, this.onUserActivity, { passive: true });
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      ['scroll', 'touchstart', 'click'].forEach((evt) => {
        window.removeEventListener(evt, this.onUserActivity);
      });
      this.removeEventListener('click', this.onDockClick);
      window.clearTimeout(this.idleTimer);
    }

    onScroll() {
      const currentY = window.scrollY;
      const delta = currentY - this.lastScrollY;
      const atTop = currentY < 40;

      if (atTop || delta < -4) {
        this.classList.remove('mobile-dock--hidden');
      } else if (delta > 4) {
        this.classList.add('mobile-dock--hidden');
      }
      this.lastScrollY = currentY;
    }

    onUserActivity() {
      this.classList.remove('mobile-dock--hidden');
      this.resetIdleTimer();
    }

    resetIdleTimer() {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = window.setTimeout(() => {
        this.classList.add('mobile-dock--hidden');
      }, this.autoHideDelay);
    }
  }

  if (!customElements.get('mobile-dock')) {
    customElements.define('mobile-dock', MobileDock);
  }
})();
